"use client";

// ─────────────────────────────────────────────────────────────────────────────
// BlogArticleBuddyTour — final beat of the trade-ideas leg.
//
// Activates on `/blog/{slug}?onboarding_blog_article=1` (set by the
// BlogBuddyTour's click_target beat on the first case-study card).
//
// Beats:
//   1. Spotlight the hero — frames the article as power-user authored,
//      refined by Xynth.
//   2. Spotlight the workflow / results image — emphasises real,
//      receipt-backed results.
//   3. Spotlight the backstory paragraphs — invites the user to read
//      the trader's story.
//   4. Spotlight the step-by-step pipeline — the meat of the strategy.
//   5. Spotlight the final master prompt — "see it in action".
//   6. Scroll back up and spotlight the Live Automation pill — the
//      bridge from guide to live signal.
//   7. (Conditional) Open the sidebar if it's collapsed.
//   8. Click_target on the sidebar Xynth logo — natural Link navigates
//      home and sessionStorage hands the home tour to "community".
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSidebar } from "@/components/ui/sidebar";
import { BuddyOverlay, type BuddyBeat } from "./BuddyOverlay";
import { useClientBuddyScript } from "./useClientBuddyScript";
import {
  BLOG_ARTICLE_TOUR_QUERY_PARAM,
  isBlogArticleTourVisit,
  setOnboardingResumeStage,
} from "@/lib/onboarding-constants";
import { dispatchStageComplete } from "@/lib/onboarding-tour";

export function BlogArticleBuddyTour() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isMobile, open, openMobile, setOpen, setOpenMobile } = useSidebar();

  const shouldRun = isBlogArticleTourVisit(searchParams);

  // Snapshot whether the sidebar was already open on first render —
  // that's the "did the viewer already have it open before the tour
  // started?" signal we want for deciding whether to insert the
  // "Open the sidebar" beat. We freeze it via ref so the snapshot
  // doesn't flip when this tour itself programmatically opens the
  // sidebar later (which would otherwise retroactively yank the beat
  // mid-run). Mirrors the same pattern in AlertPageBuddyTour.
  const includeSidebarOpenerRef = useRef<boolean | null>(null);
  const sidebarOpen = isMobile ? openMobile : open;
  if (includeSidebarOpenerRef.current === null) {
    includeSidebarOpenerRef.current = !sidebarOpen;
  }
  const includeSidebarOpener = includeSidebarOpenerRef.current;

  const beats = useMemo<BuddyBeat[]>(() => {
    if (!shouldRun) return [];
    return [
      {
        beatId: "blog_article_hero",
        trigger: { type: "client" },
        copy: {
          title: "Authored by a power user",
          body:
            "This guide comes from a Xynth power user, with the strategy and results laid out in plain English.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-author-proof']" },
        highlightOnly: true,
        bubblePlacement: "anchorBelow",
      },
      {
        beatId: "blog_article_pnl_image",
        trigger: { type: "client" },
        copy: {
          title: "Backed with real results",
          body:
            "Look at the P&L this user captured from the strategy. This is the result behind the writeup.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-pnl-image']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        scrollOffsetY: 72,
        bubblePlacement: "anchorAbove",
      },
      {
        beatId: "blog_article_backstory",
        trigger: { type: "client" },
        copy: {
          title: "Introduction",
          body:
            "The backdrop and beginning of the strategy, before it turns into a repeatable workflow.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-backstory']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        scrollOffsetY: 72,
        bubblePlacement: "anchorAbove",
      },
      {
        beatId: "blog_article_steps",
        trigger: { type: "client" },
        copy: {
          title: "The pipeline, step by step",
          body:
            "This is the actual six-step process. Scan, filter, score. Each step shows the prompt the trader ran and the AI's output side by side. You can copy any of it into your own chat.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-steps']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        scrollOffsetY: 72,
        bubblePlacement: "anchorAbove",
      },
      {
        beatId: "blog_article_final_prompt",
        trigger: { type: "client" },
        copy: {
          title: "Try it for yourself",
          body:
            "Use this prompt to run the same strategy inside your own Xynth chat.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-final-prompt']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        scrollOffsetY: 72,
        bubblePlacement: "anchorAbove",
      },
      {
        beatId: "blog_article_live_alert",
        trigger: { type: "client" },
        copy: {
          title: "See it in action",
          body:
            "This strategy is also available as a live automation, so you can open the signal version directly.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='article-live-alert']" },
        highlightOnly: true,
        scrollOffsetY: 180,
        // Pin the bubble ABOVE the highlighted alert section instead
        // of below — the section is the tallest on the page and
        // anchoring below would push the bubble off-viewport. User
        // explicitly asked for this beat's bubble to sit above the
        // section.
        bubblePlacement: "anchorAbove",
      },
      // Conditional "Open the sidebar" beat — only shown if the sidebar
      // was collapsed at first render. Skipping it when the viewer
      // already has the sidebar open avoids the dead "click the
      // sidebar-opener" pointer that would otherwise just toggle a
      // visible sidebar shut.
      ...(includeSidebarOpener
        ? [
            {
              beatId: "blog_article_sidebar_open",
              trigger: { type: "client" } as const,
              copy: {
                title: "Open the sidebar",
                body:
                  "Open the sidebar so we can head back to the Xynth home screen.",
                cta: "Next",
              },
              anchor: { kind: "css", value: "[data-tour='sidebar-opener']" } as const,
              spotlight: {
                kind: "css",
                value: "[data-tour='sidebar-opener']",
              } as const,
              action: { kind: "continue" } as const,
              highlightOnly: true,
              bubblePlacement: "anchorBelow" as const,
            },
          ]
        : []),
      {
        beatId: "blog_article_home_handoff",
        trigger: { type: "client" },
        copy: {
          title: "Back home",
          body:
            "Navigate home for the next part of the tour",
        },
        anchor: { kind: "css", value: "[data-tour='sidebar-home-logo']" },
        action: { kind: "click_target" },
        highlightOnly: true,
        bubblePlacement: "anchor",
      },
    ];
  }, [shouldRun, includeSidebarOpener]);

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(BLOG_ARTICLE_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path = window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort. */
      }
    },
    [router, searchParams],
  );

  const { beat, onContinue, onExit, done } = useClientBuddyScript(
    shouldRun ? beats : [],
    {
      onComplete: () => {
        // Belt-and-suspenders: also set the resume stage on
        // completion in case the beat-active effect below somehow
        // didn't fire (e.g. very fast forwarding through beats).
        setOnboardingResumeStage("community");
        // Tour-hub: this is the natural end of the "guides" stage.
        dispatchStageComplete("guides");
      },
      onExit: () => {
        stripQueryParam();
      },
    },
  );

  useEffect(() => {
    if (!shouldRun) return;
    if (beat?.beatId !== "blog_article_sidebar_open") return;
    if (isMobile) {
      setOpenMobile(true);
      return;
    }
    setOpen(true);
  }, [shouldRun, beat?.beatId, isMobile, setOpen, setOpenMobile]);

  // Persist the next stage the moment the home-handoff beat becomes
  // active — see AlertPageBuddyTour for the timing reasoning. tl;dr:
  // BuddyOverlay's click_target fires onContinue 75ms after the
  // user's click, by which time the natural Link has already
  // navigated; setting on beat-active guarantees the token is in
  // sessionStorage before any navigation can start.
  useEffect(() => {
    if (!shouldRun) return;
    if (beat?.beatId !== "blog_article_home_handoff") return;
    setOnboardingResumeStage("community");
  }, [shouldRun, beat?.beatId]);

  if (!shouldRun || done) return null;

  return <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />;
}
